import { Close, Delete, Edit } from '@mui/icons-material';
import { Button, Grid, Theme, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { FC, useCallback, useMemo, useState } from 'react';
// Models
import { IAddressBookRecord, IAddressBookRes } from '../../../../models';
// Fetch and Helpers
import { deleteAddressBookUser } from '../../../../fetch';
import { formatDate } from '../../../../helpers';
// Components
import { Modal, Table } from '../../../../components';
import { EditAddressBookModal } from '../modals/edit-address-book-modal';

interface IAddressBook {
  refreshData: () => void;
  addressBookData: IAddressBookRes | undefined;
  isLoadingAddressBook: boolean;
  selectedSort: string;
  sortDirection: { [key: string]: 'Asc' | 'Desc' };
  handleSort: (column: string) => void;
}

export const AddressBook: FC<IAddressBook> = ({
  refreshData,
  addressBookData,
  isLoadingAddressBook,
  selectedSort,
  sortDirection,
  handleSort
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  // Modal functions for delete //

  const openDeleteModal = useCallback((id: number) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = () => {
    setDeleteId(null);
    setIsDeleteModalOpen(false);
  };

  const handleDelete = async () => {
    if (deleteId !== null) {
      try {
        await deleteAddressBookUser(deleteId);
        refreshData();
      } catch (error) {
        enqueueSnackbar('User Deletion Failed', {
          variant: 'error'
        });
        console.error('Delete failed:', error);
      } finally {
        closeDeleteModal();
      }
    }
  };

  // Modal functions for edit //

  const openEditModal = useCallback((id: number) => {
    setEditId(id);
    setIsEditModalOpen(true);
  }, []);

  const closeEditModal = () => {
    setEditId(null);
    setIsEditModalOpen(false);
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'displayName',
        isServerSorted: selectedSort === 'DisplayName',
        isServerSortedDesc: sortDirection.DisplayName === 'Desc',
        handleClickColumn: () => handleSort('DisplayName')
      },
      {
        Header: 'All',
        accessor: (row: any) => (row.isGlobal ? 'Yes' : 'No'),
        isServerSorted: selectedSort === 'IsGlobal',
        isServerSortedDesc: sortDirection.IsGlobal === 'Desc',
        handleClickColumn: () => handleSort('IsGlobal')
      },
      {
        Header: 'Territories',
        accessor: (row: any) => (row.isGlobal ? 'All' : row.territories.join(', ')),
        sort: false
      },
      {
        Header: 'Neighborhoods',
        accessor: (row: any) => (row.isGlobal ? 'All' : row.neighborhoods.join(', ')),
        sort: false
      },
      {
        Header: 'Notifications',
        accessor: (row: IAddressBookRecord) => row.notificationTypes.join(', '),
        sort: false
      },
      {
        Header: 'Edited Date',
        accessor: (row: any) => formatDate(row.updatedDate),
        isServerSorted: selectedSort === 'UpdatedDate',
        isServerSortedDesc: sortDirection.UpdatedDate === 'Desc',
        handleClickColumn: () => handleSort('UpdatedDate')
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: any } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <Button
                id="edit"
                color="primary"
                startIcon={<Edit />}
                onClick={() => openEditModal(original.addressBookPersonId)}
              >
                Edit
              </Button>
              <Button
                className={classes.deleteButton}
                startIcon={<Delete />}
                onClick={() => openDeleteModal(original.addressBookPersonId)}
              />
            </div>
          );
        }
      }
    ];
  }, [
    selectedSort,
    sortDirection,
    handleSort,
    openEditModal,
    openDeleteModal,
    classes.actionButtons,
    classes.deleteButton
  ]);

  return (
    <>
      <>
        <Table
          columns={columns}
          data={addressBookData.records}
          hidePagination
          isLoading={isLoadingAddressBook}
        />
        <Modal open={isDeleteModalOpen} onClose={closeDeleteModal} maxWidth="xs">
          <Grid item xs={12}>
            <Typography variant="body1" my={1} fontWeight="bold">
              Are you sure you want to remove this contact from Address book assignments?
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Button type="button" variant="contained" color="primary" onClick={handleDelete}>
              Remove Contact
            </Button>
            <Button
              type="button"
              variant="contained"
              color="inherit"
              startIcon={<Close />}
              onClick={closeDeleteModal}
            >
              Cancel
            </Button>
          </Grid>
        </Modal>
        <EditAddressBookModal
          onUpdate={refreshData}
          isOpen={isEditModalOpen}
          onClose={closeEditModal}
          userId={editId}
        />
      </>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  actionButtons: {
    display: 'flex',
    textAlign: 'right'
  },
  editButton: {
    textAlign: 'right'
  },
  deleteButton: {
    marginLeft: theme.spacing(1.5),
    color: theme.palette.error.main
  }
}));
